// Mock API service for local development
// Simulates backend API without AWS dependencies

import { Job, UsageMetrics, UploadRequest, UploadResponse, DownloadUrlResponse } from '../types';
import mockJobsData from '../mockData/jobs.json';
import { getUserId } from './mockAuth';

const MOCK_JOBS_KEY = 'mockJobs';
const MOCK_USAGE_KEY = 'mockUsage';

// Simulate network delay
const delay = (ms: number = 400): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

// Initialize mock data
const initializeMockData = (): void => {
  if (!localStorage.getItem(MOCK_JOBS_KEY)) {
    localStorage.setItem(MOCK_JOBS_KEY, JSON.stringify(mockJobsData));
  }
  
  if (!localStorage.getItem(MOCK_USAGE_KEY)) {
    const initialUsage: UsageMetrics = {
      metricId: 'metrics-demo',
      transcribeMinutesUsed: 22.0,
      transcribeMinutesLimit: 100.0,
      translateCharsUsed: 12850,
      translateCharsLimit: 50000,
      s3StorageGB: 0.45,
      s3StorageLimit: 5.0,
      lambdaInvocations: 156,
      lambdaInvocationsLimit: 10000,
      lastUpdated: Date.now(),
      alertsSent: {}
    };
    localStorage.setItem(MOCK_USAGE_KEY, JSON.stringify(initialUsage));
  }
};

// Get all jobs
const getJobs = (): Job[] => {
  initializeMockData();
  const jobs = localStorage.getItem(MOCK_JOBS_KEY);
  return jobs ? JSON.parse(jobs) : [];
};

// Save jobs
const saveJobs = (jobs: Job[]): void => {
  localStorage.setItem(MOCK_JOBS_KEY, JSON.stringify(jobs));
};

// Get usage metrics
const getUsageMetrics = (): UsageMetrics => {
  initializeMockData();
  const usage = localStorage.getItem(MOCK_USAGE_KEY);
  if (!usage) {
    // Return default metrics if not found
    const defaultMetrics: UsageMetrics = {
      metricId: 'metrics-demo',
      transcribeMinutesUsed: 0,
      transcribeMinutesLimit: 100.0,
      translateCharsUsed: 0,
      translateCharsLimit: 50000,
      s3StorageGB: 0,
      s3StorageLimit: 5.0,
      lambdaInvocations: 0,
      lambdaInvocationsLimit: 10000,
      lastUpdated: Date.now(),
      alertsSent: {}
    };
    return defaultMetrics;
  }
  return JSON.parse(usage);
};

// Save usage metrics
const saveUsageMetrics = (usage: UsageMetrics): void => {
  localStorage.setItem(MOCK_USAGE_KEY, JSON.stringify(usage));
};

// Mock API Client
class MockApiClient {
  // Upload endpoints
  async requestUpload(request: UploadRequest): Promise<UploadResponse> {
    await delay(300);
    
    const userId = getUserId();
    if (!userId) {
      throw new Error('Not authenticated');
    }
    
    // Generate job ID
    const jobId = `job-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Create new job
    const newJob: Job = {
      jobId,
      userId,
      status: 'uploaded',
      mediaType: request.contentType.startsWith('video/') ? 'video' : 'audio',
      fileName: request.fileName,
      fileSize: request.fileSize,
      duration: 0, // Will be updated after processing
      uploadTimestamp: Date.now(),
      mediaS3Key: `uploads/${userId}/${request.fileName}`,
    };
    
    // Save job
    const jobs = getJobs();
    jobs.unshift(newJob);
    saveJobs(jobs);
    
    // Generate mock presigned URL
    const uploadUrl = `https://mock-s3-bucket.s3.amazonaws.com/uploads/${userId}/${request.fileName}?mock=true`;
    
    // Simulate job processing after a delay
    setTimeout(() => {
      this.simulateJobProcessing(jobId);
    }, 5000);
    
    return {
      jobId,
      uploadUrl,
      expiresIn: 3600,
    };
  }

  // Simulate job processing through different stages
  private async simulateJobProcessing(jobId: string): Promise<void> {
    const jobs = getJobs();
    const jobIndex = jobs.findIndex(j => j.jobId === jobId);
    
    if (jobIndex === -1) return;
    
    const job = jobs[jobIndex];
    
    // Stage 1: Audio extraction (for videos)
    if (job.mediaType === 'video') {
      await new Promise(resolve => setTimeout(resolve, 3000));
      job.status = 'audio_extracted';
      job.audioS3Key = `audio/${job.userId}/${job.fileName.replace(/\.[^/.]+$/, '.mp3')}`;
      job.audioExtractedAt = Date.now();
      job.duration = Math.floor(Math.random() * 300) + 60; // Random duration 60-360s
      jobs[jobIndex] = job;
      saveJobs(jobs);
    } else {
      job.audioS3Key = job.mediaS3Key;
      job.duration = Math.floor(Math.random() * 300) + 60;
      jobs[jobIndex] = job;
      saveJobs(jobs);
    }
    
    // Stage 2: Transcription
    await new Promise(resolve => setTimeout(resolve, 5000));
    job.status = 'transcribing';
    job.transcribeJobName = `transcribe-${jobId}`;
    jobs[jobIndex] = job;
    saveJobs(jobs);
    
    await new Promise(resolve => setTimeout(resolve, 8000));
    job.status = 'translating';
    job.transcriptS3Keys = {
      sw: `transcripts/${job.userId}/${job.fileName.replace(/\.[^/.]+$/, '-sw.txt')}`,
      en: `transcripts/${job.userId}/${job.fileName.replace(/\.[^/.]+$/, '-en.txt')}`,
      fr: `transcripts/${job.userId}/${job.fileName.replace(/\.[^/.]+$/, '-fr.txt')}`,
    };
    job.transcribedAt = Date.now();
    job.transcribeDuration = job.duration / 60; // Convert to minutes
    jobs[jobIndex] = job;
    saveJobs(jobs);
    
    // Update usage metrics
    const usage = getUsageMetrics();
    usage.transcribeMinutesUsed += job.transcribeDuration || 0;
    usage.lastUpdated = Date.now();
    saveUsageMetrics(usage);
    
    // Stage 3: Translation
    await new Promise(resolve => setTimeout(resolve, 6000));
    job.status = 'generating_output';
    job.translatedAt = Date.now();
    job.translateCharCount = Math.floor(Math.random() * 3000) + 1000; // Random char count
    jobs[jobIndex] = job;
    saveJobs(jobs);
    
    // Update usage metrics
    usage.translateCharsUsed += job.translateCharCount || 0;
    usage.lastUpdated = Date.now();
    saveUsageMetrics(usage);
    
    // Stage 4: Generate output files
    await new Promise(resolve => setTimeout(resolve, 4000));
    job.status = 'completed';
    job.outputS3Keys = [
      `outputs/${job.userId}/${job.fileName.replace(/\.[^/.]+$/, '-sw.srt')}`,
      `outputs/${job.userId}/${job.fileName.replace(/\.[^/.]+$/, '-en.srt')}`,
      `outputs/${job.userId}/${job.fileName.replace(/\.[^/.]+$/, '-fr.srt')}`,
    ];
    job.completedAt = Date.now();
    job.completionTimestamp = Date.now();
    jobs[jobIndex] = job;
    saveJobs(jobs);
    
    // Update storage usage
    usage.s3StorageGB += (job.fileSize / (1024 * 1024 * 1024));
    usage.lambdaInvocations += 8; // Simulate lambda invocations
    usage.lastUpdated = Date.now();
    saveUsageMetrics(usage);
  }

  // Job endpoints
  async getJobs(): Promise<Job[]> {
    await delay();
    
    const userId = getUserId();
    if (!userId) {
      throw new Error('Not authenticated');
    }
    
    const allJobs = getJobs();
    
    // Filter jobs for current user
    return allJobs.filter(job => job.userId === userId);
  }

  async getJob(jobId: string): Promise<Job> {
    await delay(200);
    
    const userId = getUserId();
    if (!userId) {
      throw new Error('Not authenticated');
    }
    
    const jobs = getJobs();
    const job = jobs.find(j => j.jobId === jobId && j.userId === userId);
    
    if (!job) {
      throw new Error('Job not found');
    }
    
    return job;
  }

  async deleteJob(jobId: string): Promise<void> {
    await delay(300);
    
    const userId = getUserId();
    if (!userId) {
      throw new Error('Not authenticated');
    }
    
    const jobs = getJobs();
    const filteredJobs = jobs.filter(j => !(j.jobId === jobId && j.userId === userId));
    
    if (filteredJobs.length === jobs.length) {
      throw new Error('Job not found');
    }
    
    saveJobs(filteredJobs);
    
    // Update storage usage
    const job = jobs.find(j => j.jobId === jobId);
    if (job) {
      const usage = getUsageMetrics();
      usage.s3StorageGB = Math.max(0, usage.s3StorageGB - (job.fileSize / (1024 * 1024 * 1024)));
      usage.lastUpdated = Date.now();
      saveUsageMetrics(usage);
    }
  }

  async getDownloadUrl(jobId: string, fileType: string): Promise<DownloadUrlResponse> {
    await delay(200);
    
    const userId = getUserId();
    if (!userId) {
      throw new Error('Not authenticated');
    }
    
    const jobs = getJobs();
    const job = jobs.find(j => j.jobId === jobId && j.userId === userId);
    
    if (!job) {
      throw new Error('Job not found');
    }
    
    // Generate mock download URL based on file type
    let s3Key = '';
    switch (fileType) {
      case 'transcript-sw':
        s3Key = job.transcriptS3Keys?.sw || '';
        break;
      case 'transcript-en':
        s3Key = job.transcriptS3Keys?.en || '';
        break;
      case 'transcript-fr':
        s3Key = job.transcriptS3Keys?.fr || '';
        break;
      case 'subtitle-sw':
        s3Key = job.outputS3Keys?.[0] || '';
        break;
      case 'subtitle-en':
        s3Key = job.outputS3Keys?.[1] || '';
        break;
      case 'subtitle-fr':
        s3Key = job.outputS3Keys?.[2] || '';
        break;
      case 'media':
        s3Key = job.mediaS3Key;
        break;
      case 'audio':
        s3Key = job.audioS3Key || '';
        break;
      default:
        throw new Error('Invalid file type');
    }
    
    if (!s3Key) {
      throw new Error('File not available');
    }
    
    return {
      downloadUrl: `https://mock-s3-bucket.s3.amazonaws.com/${s3Key}?mock=true`,
      expiresIn: 3600,
    };
  }

  // Usage endpoints
  async getUsage(): Promise<UsageMetrics> {
    await delay(300);
    
    const userId = getUserId();
    if (!userId) {
      throw new Error('Not authenticated');
    }
    
    return getUsageMetrics();
  }

  async getAdminUsage(): Promise<UsageMetrics> {
    await delay(300);
    
    // In mock mode, return same as regular usage
    return getUsageMetrics();
  }

  // Upload file to S3 (mock)
  async uploadToS3(url: string, file: File): Promise<void> {
    await delay(1000 + Math.random() * 2000); // Simulate upload time
    
    // In mock mode, we don't actually upload anything
    // Just simulate the delay
    console.log(`[Mock] Uploaded ${file.name} (${file.size} bytes) to ${url}`);
  }

  // Admin endpoints
  async getAdminUsers(approvalStatus?: string): Promise<any[]> {
    await delay(300);

    // Mock users data
    const mockUsers = [
      {
        userId: 'user-1',
        email: 'john@example.com',
        registrationDate: Date.now() - 86400000 * 2,
        approvalStatus: 'pending',
        totalJobsSubmitted: 0,
        activeJobCount: 0,
      },
      {
        userId: 'user-2',
        email: 'jane@example.com',
        registrationDate: Date.now() - 86400000 * 5,
        approvalStatus: 'approved',
        totalJobsSubmitted: 3,
        activeJobCount: 1,
      },
      {
        userId: 'user-3',
        email: 'bob@example.com',
        registrationDate: Date.now() - 86400000 * 10,
        approvalStatus: 'rejected',
        rejectionReason: 'Suspicious activity detected',
        totalJobsSubmitted: 0,
        activeJobCount: 0,
      },
    ];

    if (approvalStatus) {
      return mockUsers.filter(u => (u.approvalStatus || 'pending') === approvalStatus);
    }

    return mockUsers;
  }

  async updateUserApproval(userId: string, approvalStatus: 'approved' | 'rejected', rejectionReason?: string): Promise<void> {
    await delay(300);
    console.log('Mock: Updated user approval', { userId, approvalStatus, rejectionReason });
    // In a real implementation, this would update the user in the database
  }
}

export const mockApiClient = new MockApiClient();
